import { ChevronLeft, ChevronRight, Filter, Handshake, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { PRODUCT_LABEL, PRODUCT_ORDER, PRODUCT_SLUG, countryName, fmt, fmtDate, paymentLabel } from "../lib/constants";
import { clampRange, daysBetween, eventsInRange, fmtRange, isSingleDay, monthStart, shiftDay, singleDay, todayIso } from "../lib/dayView";
import { PartnerBadge } from "./PartnerBadge";
import { OwnerBreakdown, shortName } from "./OwnerBreakdown";

const weekday = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { weekday: "long" });
const weekdayShort = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short" });
const timeOf = (iso) => new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
const dayOf = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
const sameRange = (a, b) => !!a && !!b && a.from === b.from && a.to === b.to;

const NavButton = ({ testId, onClick, disabled, title, children }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    disabled={disabled}
    title={title}
    className="inline-flex items-center justify-center w-9 h-9 sm:w-8 sm:h-8 shrink-0 rounded-full border border-panel-border bg-panel text-slate-600 hover:text-ink hover:border-slate-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
  >
    {children}
  </button>
);

const Quick = ({ testId, onClick, active, disabled, children }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    disabled={disabled}
    className={`shrink-0 rounded-full border px-3 py-1.5 sm:py-1 text-xs font-semibold transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${active ? "border-brand bg-brand/5 text-brand" : "border-panel-border bg-panel text-slate-600 hover:border-slate-400"}`}
  >
    {children}
  </button>
);

const DateInput = ({ testId, label, value, min, max, onChange }) => (
  <label className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 min-w-0 text-xs text-slate-500">
    <span className="shrink-0">{label}</span>
    <input
      type="date"
      data-testid={testId}
      value={value}
      min={min}
      max={max}
      onChange={(e) => e.target.value && onChange(e.target.value)}
      className="num w-full min-w-0 sm:w-auto rounded-md border border-panel-border bg-panel px-2 py-1.5 sm:py-1 text-xs text-ink outline-none focus:border-brand"
    />
  </label>
);

// Level 1 drill-down: everything onboarded on one day / in a from–to range, optionally for one country.
// Layout is mobile-first: stacked header, scrollable quick ranges, two-line venue rows, full-width CTA.
export const DayPanel = ({ view, range, onChangeRange, onClose, onShowOnBoard, countFrom, ownersPeriod, showOwners = false }) => {
  const open = !!range;
  const today = todayIso();
  const minDay = view?.recent_from || view?.daily_series?.[0]?.date || today;
  const events = open ? eventsInRange(view, range) : [];
  // upsell rows = products sold to venues that were already clients before the cutoff: products yes, new venues no
  const newVenues = events.filter((e) => !e.upsell);
  const upsells = events.filter((e) => e.upsell);
  const upsellProducts = upsells.reduce((s, e) => s + (e.products?.length || 0), 0);
  const upsellVenues = new Set(upsells.map((e) => e.venue_id)).size;
  const products = Object.fromEntries(PRODUCT_ORDER.map((p) => [p, 0]));
  for (const e of events) for (const p of e.products || []) products[p] += 1;
  const productsSold = Object.values(products).reduce((s, n) => s + n, 0);
  const single = isSingleDay(range);
  const span = open ? daysBetween(range.from, range.to) : 1;
  const country = range?.country || null;
  const partnersOnly = !!range?.partner;
  const owner = range?.owner || null;
  const countryRow = country ? (view?.countries || []).find((c) => c.code === country) : null;
  const keepFilters = (r) => ({ ...r, ...(country ? { country } : {}), ...(partnersOnly ? { partner: true } : {}), ...(owner ? { owner } : {}) });
  const set = (r) => onChangeRange(clampRange(keepFilters(r), minDay, today));
  const togglePartners = () => onChangeRange({ ...range, partner: partnersOnly ? undefined : true });
  const allInRange = open ? eventsInRange(view, { ...range, partner: undefined }).filter((e) => !e.upsell) : [];
  const partnersInRange = allInRange.filter((e) => e.partner).length;
  const yesterday = singleDay(shiftDay(today, -1));
  const last7 = { from: shiftDay(today, -6), to: today };
  const thisMonth = { from: monthStart(today), to: today };
  const sinceCutoff = countFrom ? { from: countFrom, to: today } : null;
  const partnerCount = newVenues.filter((e) => e.partner).length;
  const perDay = single ? [] : Object.entries(newVenues.reduce((acc, e) => ({ ...acc, [e.won_day]: (acc[e.won_day] || 0) + 1 }), {})).sort();
  // venues grouped by country (largest first), each with its own product breakdown
  const groups = Object.values(
    events.reduce((acc, e) => {
      const g = (acc[e.country] ||= { code: e.country, events: [], venues: 0, upsells: 0, products: {} });
      g.events.push(e);
      if (e.upsell) g.upsells += 1;
      else g.venues += 1;
      for (const p of e.products || []) g.products[p] = (g.products[p] || 0) + 1;
      return acc;
    }, {})
  ).sort((a, b) => b.venues - a.venues || b.events.length - a.events.length || a.code.localeCompare(b.code));

  const title = !open ? "" : single ? `${weekday(range.from)} ${fmtDate(range.from)}` : `${fmtRange(range)} · ${span} days`;
  const titleShort = !open ? "" : single ? `${weekdayShort(range.from)} ${fmtDate(range.from)}` : `${fmtRange(range)} · ${span}d`;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl w-[calc(100vw-1rem)] sm:w-full p-0 gap-0 overflow-hidden bg-panel text-ink" data-testid="day-panel">
        <div className="flex flex-col min-w-0 min-h-0 max-h-[94vh] sm:max-h-[90vh] gap-3 sm:gap-4 p-4 sm:p-6 pt-5 sm:pt-6">
        <DialogHeader className="shrink-0 min-w-0 space-y-2 sm:space-y-2 text-left pr-7 sm:pr-8">
          <div className="flex items-center gap-2 sm:gap-3">
            <NavButton testId="day-panel-prev" onClick={() => set({ from: shiftDay(range.from, -span), to: shiftDay(range.to, -span) })} disabled={!open || range.from <= minDay} title={single ? "Previous day" : `Previous ${span} days`}><ChevronLeft className="w-4 h-4" /></NavButton>
            <DialogTitle className="flex-1 min-w-0 truncate text-center sm:text-left text-base sm:text-xl font-bold num" data-testid="day-panel-title">
              <span className="sm:hidden">{titleShort}</span>
              <span className="hidden sm:inline">{title}</span>
            </DialogTitle>
            <NavButton testId="day-panel-next" onClick={() => set({ from: shiftDay(range.from, span), to: shiftDay(range.to, span) })} disabled={!open || range.to >= today} title={single ? "Next day" : `Next ${span} days`}><ChevronRight className="w-4 h-4" /></NavButton>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <div className="flex items-center gap-2 overflow-x-auto thin-scroll -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap pb-0.5" data-testid="day-panel-quick">
              {country && (
                <button
                  type="button"
                  data-testid="day-panel-country-chip"
                  onClick={() => onChangeRange({ ...range, country: undefined })}
                  title="Remove the country filter"
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-ink text-page px-3 py-1.5 sm:py-1 text-xs font-semibold hover:bg-ink/80 transition-colors"
                >
                  <span className="num text-page/70">{country === "ZZ" ? "—" : country}</span> {country === "ZZ" ? "No country" : countryName(country)} <X className="w-3 h-3" />
                </button>
              )}
              <button
                type="button"
                data-testid="day-panel-partner-toggle"
                aria-pressed={partnersOnly}
                onClick={togglePartners}
                title={partnersOnly ? "Showing partner-sourced venues only – click to show all" : "Show only venues that came through partner pipelines"}
                className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 sm:py-1 text-xs font-semibold transition-colors ${partnersOnly ? "border-amber-400 bg-amber-100 text-amber-900" : "border-panel-border bg-panel text-slate-600 hover:border-amber-400 hover:text-amber-800"}`}
              >
                <Handshake className="w-3.5 h-3.5" /> Partners only
                <span className={`num rounded-full px-1.5 text-[10px] ${partnersOnly ? "bg-amber-200 text-amber-900" : "bg-slate-100 text-slate-500"}`} data-testid="day-panel-partner-toggle-count">{partnersInRange}</span>
                {partnersOnly && <X className="w-3 h-3" />}
              </button>
              <Quick testId="day-panel-yesterday" onClick={() => set(yesterday)} active={sameRange(range, yesterday)}>Yesterday</Quick>
              <Quick testId="day-panel-today" onClick={() => set(singleDay(today))} active={sameRange(range, singleDay(today))}>Today</Quick>
              <Quick testId="day-panel-last7" onClick={() => set(last7)} active={sameRange(range, last7)}>Last 7 days</Quick>
              <Quick testId="day-panel-this-month" onClick={() => set(thisMonth)} active={sameRange(range, thisMonth)}>This month</Quick>
              {sinceCutoff && countFrom !== thisMonth.from && (
                <Quick testId="day-panel-since-cutoff" onClick={() => set(sinceCutoff)} active={sameRange(range, sinceCutoff)} disabled={countFrom < minDay}>Since {dayOf(countFrom)}</Quick>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:gap-3 sm:ml-auto" data-testid="day-panel-range-inputs">
              <DateInput testId="day-panel-from" label="From" value={range?.from || ""} min={minDay} max={today} onChange={(v) => set({ from: v, to: range.to })} />
              <DateInput testId="day-panel-to" label="To" value={range?.to || ""} min={minDay} max={today} onChange={(v) => set({ from: range.from, to: v })} />
            </div>
          </div>
          <DialogDescription className="text-slate-600 text-left text-sm" data-testid="day-panel-summary">
            {partnersOnly ? <span className="text-amber-800 font-semibold">Partners only · </span> : null}
            {owner ? <span className="font-semibold text-ink" data-testid="day-panel-owner-filter">{owner} · </span> : null}
            <b className="num text-ink">{fmt(newVenues.length)}</b> {partnersOnly ? "partner" : "new"} {newVenues.length === 1 ? "venue" : "venues"} · <b className="num text-ink">{fmt(productsSold)}</b> {productsSold === 1 ? "product" : "products"} sold
            {upsellProducts ? <span className="text-sky-700" data-testid="day-panel-upsell-count" title="Products sold in this range to venues that were already clients before the cutoff – counted as products, not as new venues"> · of which <b className="num">{fmt(upsellProducts)}</b> upsell to {fmt(upsellVenues)} existing {upsellVenues === 1 ? "venue" : "venues"}</span> : null}
            {groups.length ? <span className="text-slate-400"> · {groups.length} {groups.length === 1 ? "country" : "countries"}</span> : null}
            {!partnersOnly && partnerCount ? <span className="text-amber-700" data-testid="day-panel-partner-count"> · {partnerCount} via partners</span> : null}
            {partnersOnly && allInRange.length ? <span className="text-slate-400"> · {Math.round((newVenues.length / allInRange.length) * 100)}% of {fmt(allInRange.length)} venues</span> : null}
            {!single && newVenues.length ? <span className="text-slate-400"> · {(newVenues.length / span).toFixed(1)} per day</span> : null}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap shrink-0 gap-1.5 sm:gap-2" data-testid="day-panel-products">
          {PRODUCT_ORDER.filter((p) => p !== "Others" || products[p]).map((p) => (
            <span
              key={p}
              data-testid={`day-product-${PRODUCT_SLUG(p)}`}
              className={`items-center gap-1.5 rounded-full border px-2.5 sm:px-3 py-1 text-xs font-semibold ${products[p] ? "inline-flex border-brand/30 bg-brand/5 text-ink" : "hidden sm:inline-flex border-panel-border text-slate-400"}`}
            >
              {PRODUCT_LABEL[p]} <b className={`num ${products[p] ? "text-brand" : "text-slate-400"}`}>{products[p]}</b>
            </span>
          ))}
        </div>
        {showOwners && countryRow && (
          <OwnerBreakdown owners={countryRow.owners} total={countryRow.total} period={ownersPeriod} active={owner} onPick={(name) => onChangeRange({ ...range, owner: name || undefined })} />
        )}
        {perDay.length > 0 && (
          <div className="flex shrink-0 gap-x-3 gap-y-1 text-[11px] text-slate-500 overflow-x-auto thin-scroll sm:flex-wrap -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="day-panel-per-day">
            {perDay.map(([d, n]) => (
              <button key={d} type="button" data-testid={`day-panel-day-${d}`} onClick={() => set(singleDay(d))} className="num shrink-0 hover:text-brand transition-colors" title={`Show only ${fmtDate(d)}`}>
                {dayOf(d)} <b className="text-ink">{n}</b>
              </button>
            ))}
          </div>
        )}

        <div className="flex-1 min-h-[30vh] sm:min-h-0 overflow-y-auto thin-scroll panel" data-testid="day-panel-list">
          {events.length === 0 && (
            <div className="p-4 text-sm text-slate-500" data-testid="day-panel-empty">
              {owner ? `No venues closed by ${owner} ` : partnersOnly ? "No partner-sourced venues " : "No venues onboarded "}{single ? "on this day" : "in this range"}.
            </div>
          )}
          {groups.map((g) => (
            <div key={g.code} data-testid={`day-group-${g.code}`} className="border-b border-panel-border last:border-b-0">
              <div className="sticky top-0 z-10 flex items-center gap-2 flex-wrap bg-slate-50/95 backdrop-blur px-3 sm:px-4 py-1.5 text-xs border-b border-panel-border">
                <span className="num font-bold text-slate-400 w-6">{g.code === "ZZ" ? "—" : g.code}</span>
                <span className="font-bold text-ink">{g.code === "ZZ" ? "No country" : countryName(g.code)}</span>
                <span className="text-slate-500">· <b className="num text-ink" data-testid={`day-group-${g.code}-count`}>{g.venues}</b> {g.venues === 1 ? "venue" : "venues"}</span>
                {g.upsells ? <span className="text-sky-700" data-testid={`day-group-${g.code}-upsells`}>· <b className="num">{g.upsells}</b> {g.upsells === 1 ? "upsell" : "upsells"}</span> : null}
                <span className="flex flex-wrap gap-1 ml-auto">
                  {PRODUCT_ORDER.filter((p) => g.products[p]).map((p) => (
                    <span key={p} className="rounded-full border border-brand/30 bg-panel px-2 py-0.5 text-[11px] font-semibold text-ink">
                      {PRODUCT_LABEL[p]} <b className="num text-brand">{g.products[p]}</b>
                    </span>
                  ))}
                </span>
              </div>
              <div className="divide-y divide-panel-border">
                {g.events.map((e) => (
                  <div key={e.id} data-testid={`day-venue-${e.id}`} data-partner={e.partner ? "true" : undefined} data-upsell={e.upsell ? "true" : undefined} className={`pl-3 sm:pl-12 pr-3 sm:pr-4 py-2 text-sm ${e.partner && !e.upsell ? "bg-amber-50/70" : e.upsell ? "bg-sky-50/60" : ""}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-semibold text-ink truncate">{e.name}</span>
                      {e.upsell ? (
                        <span data-testid={`day-venue-upsell-${e.id}`} title="Product sold to a venue that was already a client before the cutoff – counted in products, not as a new venue" className="rounded-full bg-sky-100 text-sky-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide shrink-0">upsell<span className="hidden sm:inline"> · existing venue</span></span>
                      ) : (
                        <PartnerBadge label={e.partner} testId={`day-venue-partner-${e.id}`} />
                      )}
                      <span className="ml-auto num text-xs text-slate-400 shrink-0">{single ? timeOf(e.won_at) : dayOf(e.won_day)}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-0.5 min-w-0">
                      {showOwners && e.owner && (
                        <span data-testid={`day-venue-owner-${e.id}`} title={`Deal owner: ${e.owner}`} className="rounded-full bg-ink/5 text-slate-600 px-2 py-0.5 text-[11px] font-semibold shrink-0">{shortName(e.owner)}</span>
                      )}
                      <span className="text-xs text-slate-500 truncate max-w-full">
                        {e.upsell ? e.deal_name : e.merchant && e.merchant !== e.name ? e.merchant : ""}
                        {!e.upsell && e.deals > 1 ? `${e.merchant && e.merchant !== e.name ? " · " : ""}${e.deals} deals` : ""}
                      </span>
                      <span className="flex flex-wrap gap-1 sm:ml-auto">
                        {(e.products || []).map((p) => (
                          <span key={p} className="rounded-full bg-slate-100 text-slate-700 px-2 py-0.5 text-[11px] font-semibold" data-testid={p === "Payments" && e.payment ? `day-venue-payment-${e.id}` : undefined} title={p === "Payments" && e.payment ? `Payments provider / channel from the line items: ${paymentLabel(e.payment)}` : undefined}>
                            {PRODUCT_LABEL[p] || p}{p === "Payments" && e.payment ? <span className="text-emerald-800 font-bold"> · {paymentLabel(e.payment)}</span> : null}
                          </span>
                        ))}
                        {!e.products?.length && <span className="text-[11px] text-slate-400">no line items</span>}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="flex shrink-0 flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 pt-0 sm:pt-1">
          <span className="hidden sm:block text-xs text-slate-400">Click a bar in the chart, pick From / To, or use the quick ranges.</span>
          <button
            type="button"
            data-testid="day-panel-show-on-board"
            onClick={() => onShowOnBoard(range)}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-ink text-page px-4 py-2.5 sm:py-2 text-sm font-semibold hover:bg-ink/85 transition-colors"
          >
            <Filter className="w-4 h-4" /> Filter the board to {partnersOnly ? "these partner venues" : single ? "this day" : "this range"}
          </button>
        </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
